/** The recorder engine's public types (decision 0051; wire format docs/recorder-api.md). Framework-free:
 * the screens (session B) call the engine and render what its events say. */

export type Track = "mic" | "call";
export type EngineState = "idle" | "starting" | "recording" | "pausing" | "paused" | "stopping" | "uploading" | "done" | "quarantined" | "error";

/** Exactly the POST /api/recorder response. */
export interface Started {
  recording_id: string;
  meeting_id: string;
  token: string;
  token_expires_at: string;
  key: string; // base64 of 32 bytes: the device buffer's AES-GCM key. Memory only.
  max_chunks_per_track: number;
  chunk_seconds: number;
  recording_ends_at: string;
  recording_limit_seconds?: number; // the launch cap on one recording (55 minutes); the engine stops itself there
  recording_warn_seconds?: number; // when to warn that the cap is near (50 minutes)
  unsent_expires_at: string;
  host: string; // a short hash of the signed-in host: marks whose buffered audio this is
}

/** Audio only. The engine stops and removes any video track and refuses a stream that still has one. */
export interface Streams {
  mic: MediaStream;
  call: MediaStream | null;
}

export interface Unsent {
  minutes: number; // the longer track's unsent audio
  chunks: number;
  oldest_age_s: number;
  expires_at: string;
  warn_24h: boolean; // 24 hours or less remain
}

export type UnsentCause = "offline" | "signed_out" | "refused" | "slow" | null;

export type Warning =
  | { kind: "call_stopped" }
  | { kind: "call_silent"; seconds: number }
  | { kind: "unsent"; level: "info" | "warn" | "error"; unsent: Unsent; cause: UnsentCause }
  | { kind: "expiring"; expires_at: string }
  | { kind: "signed_out" }
  | { kind: "recording_limit"; seconds_left: number } // the cap is near: a recording ends at 55 minutes
  | { kind: "recording_limit_reached" } // the engine reached the cap and is stopping the recording itself
  | { kind: "uploads_paused"; code: string } // the server or storage turned an upload down; the engine keeps retrying (not an error)
  | { kind: "storage_error"; detail: string }
  // CAP-009 Pause/Continue (A s.21): a pause past 30 minutes (or a lock, sleep or reload while paused) ended the recording.
  | { kind: "pause_expired" }
  // The server didn't record the pause yet (D: retried in the background with the same audio_ms until it does).
  | { kind: "pause_unsaved" }
  | { kind: "pause_saved" };

export interface EngineEvents {
  onState(state: EngineState): void;
  onLevels(levels: { mic: number; call: number | null }): void;
  onElapsed(ms: number): void;
  onWarning(warning: Warning): void;
  onUnsent(unsent: Unsent | null): void;
  onError(code: string, message: string): void;
}

export interface StopResult {
  status: "complete" | "awaiting_upload" | "quarantined";
}

export interface RecoverableRecording {
  recording_id: string;
  started_at: string;
  unsent: Unsent;
}

/** What the engine needs from the network. The default implementation talks to the console (see api.ts). */
export interface RecorderApi {
  // Bearer-token routes (no cookie)
  chunkUrl(token: string, recordingId: string, body: ChunkRequest): Promise<SignedPut>;
  stop(token: string, recordingId: string, body: StopRequest): Promise<StopResponse>;
  complete(token: string, recordingId: string): Promise<StopResponse>;
  diagnostics(token: string, recordingId: string, body: Record<string, unknown>): Promise<void>;
  // CAP-009 (A s.21): pause and continue, bearer-token routes like chunk-url and stop.
  pause(token: string, recordingId: string, body: { audio_ms: number }): Promise<{ paused: true; paused_at: string; pause_expires_at: string }>;
  continueRecording(token: string, recordingId: string): Promise<{ paused: false; resumed_at: string; paused_seconds: number }>;
  put(url: string, headers: Record<string, string>, body: Uint8Array): Promise<number>; // the HTTP status of the S3 PUT
  // Member (cookie + CSRF) routes
  unsent(): Promise<UnsentList>; // the signed-in host's open recordings, and who that host is
  resume(recordingId: string): Promise<ResumeResponse>;
  discard(recordingId: string, keepUploaded: boolean): Promise<void>;
}

export interface UnsentList {
  host: string;
  recordings: UnsentRow[];
}

export interface UnsentRow {
  recording_id: string;
  expires_at: string;
  warn_at: string;
  status: string;
}

export interface ChunkRequest {
  track: Track;
  seq: number;
  bytes: number;
  started_ms: number;
  duration_ms: number;
  content_type: string;
}
export interface SignedPut {
  url: string;
  method: "PUT";
  headers: Record<string, string>;
  expires_at: string;
}
export interface StopRequest {
  manifest: Partial<Record<Track, number>>;
  ended_ms: number;
  track_offsets_ms: Partial<Record<Track, number>>;
}
export interface StopResponse {
  status: "complete" | "awaiting_upload" | "quarantined";
  missing: Partial<Record<Track, number[]>>;
}
export interface ResumeResponse {
  token: string;
  token_expires_at: string;
  key: string;
  manifest: Partial<Record<Track, number>> | null;
  stored: Partial<Record<Track, number>>;
  recording_id: string;
  unsent_expires_at: string;
}

/** An API error with the HTTP status and the server's stable code. */
export class ApiFailure extends Error {
  constructor(readonly status: number, readonly code: string, message = "") {
    super(message || code);
  }
}
